import React, { useEffect, useRef, useState } from 'react';

// Helcim Pay/Helcim.js iframe checkout. We initialize a session server-side
// (gets a checkoutToken + secretToken), inject the helcim-pay.js script, and
// open the iframe. On success Helcim posts back a transaction id which we
// verify via /api/helcim/confirm before marking the order paid.
//
// Loading the script lazily on mount means there's no extra weight on pages
// that don't need it.

const HELCIM_SCRIPT_URL = 'https://secure.helcim.app/helcim-pay/services/start.js';

function loadScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.appendHelcimPayIframe || document.querySelector(`script[src="${HELCIM_SCRIPT_URL}"]`)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = HELCIM_SCRIPT_URL;
    s.async = true;
    s.onload = resolve; s.onerror = () => reject(new Error('Failed to load Helcim.js'));
    document.head.appendChild(s);
  });
}

export default function HelcimCardForm({ amount, currency = 'CAD', orderId, orderNumber, preauth, mode, onSuccess, onError }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const messageHandlerRef = useRef(null);

  useEffect(() => () => {
    if (messageHandlerRef.current) window.removeEventListener('message', messageHandlerRef.current);
  }, []);

  const start = async () => {
    setErr(null); setBusy(true);
    try {
      await loadScript();

      // 1. Server creates the session
      const initRes = await fetch('/api/helcim/intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                   ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}) },
        body: JSON.stringify({ amount, currency, order_number: orderNumber, preauth, mode }),
      });
      const init = await initRes.json();
      if (!initRes.ok) throw new Error(init.error || 'failed to initialize Helcim session');

      // 2. Open the Helcim iframe
      if (typeof window.appendHelcimPayIframe !== 'function') {
        throw new Error('Helcim.js not available');
      }
      window.appendHelcimPayIframe(init.providerId, true);

      // 3. Listen for completion message
      const handler = async (event) => {
        if (!event?.data) return;
        const payload = typeof event.data === 'string' ? safeJson(event.data) : event.data;
        if (!payload) return;
        // Helcim posts an event of shape { eventName: 'helcim-pay-js-' + checkoutToken, eventStatus, eventMessage }
        if (typeof payload.eventName !== 'string' || !payload.eventName.includes('helcim-pay-js')) return;
        if (payload.eventStatus !== 'SUCCESS') {
          setErr(payload.eventMessage || 'Payment did not complete');
          setBusy(false);
          return;
        }
        const txData = safeJson(payload.eventMessage) || {};
        const transactionId = txData?.data?.data?.transactionId || txData?.data?.transactionId;
        if (!transactionId) { setErr('No transaction id from Helcim'); setBusy(false); return; }

        // 4. Server-verify
        try {
          const r = await fetch('/api/helcim/confirm', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json',
                       ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}) },
            body: JSON.stringify({ transaction_id: transactionId, order_id: orderId }),
          });
          const j = await r.json();
          if (!r.ok) throw new Error(j.error || 'Server failed to confirm transaction');
          onSuccess?.({ transactionId, ...j });
        } catch (e) { setErr(e.message); onError?.(e); }
        finally { setBusy(false); window.removeEventListener('message', handler); }
      };
      messageHandlerRef.current = handler;
      window.addEventListener('message', handler);
    } catch (e) { setErr(e.message); onError?.(e); setBusy(false); }
  };

  return (
    <div>
      <button type="button" onClick={start} disabled={busy} className="btn btn-primary"
        style={{ background: '#0d5c3a', padding: '12px 20px', fontSize: 15 }}>
        {busy ? 'Opening secure card form…' : `Pay $${Number(amount || 0).toFixed(2)} via Helcim`}
      </button>
      {err && <div style={{ marginTop: 8, color: 'var(--color-danger, #b91c1c)', fontSize: 13 }}>{err}</div>}
      <p style={{ marginTop: 8, fontSize: 11, color: 'var(--text-light, #64748b)' }}>
        Card details are entered in a secure iframe hosted by Helcim — SilverGames never sees your card number.
      </p>
    </div>
  );
}

function safeJson(s) { try { return JSON.parse(s); } catch (_) { return null; } }
